import React, { useState, useEffect } from 'react';
import { parseVerilog, parseYosysJson } from './astParser';

const TimingAnalyzer = ({ parsedGraph: parentGraph, theme, code }) => {
  const [corner, setCorner] = useState('typical');
  const [expandedPathId, setExpandedPathId] = useState(null);
  const [localGraph, setLocalGraph] = useState(null);

  // Self-parse code if parent didn't provide parsedGraph
  useEffect(() => {
    if (!parentGraph && code) {
      try {
        let graph;
        if (code.trim().startsWith('{')) {
          const jsonObj = JSON.parse(code);
          graph = parseYosysJson(jsonObj);
        } else {
          graph = parseVerilog(code);
        }
        if (graph) {
          setLocalGraph(graph);
        }
      } catch (err) {
        console.error("Timing auto-parse error:", err);
      }
    }
  }, [code, parentGraph]);

  const activeGraph = parentGraph || localGraph || { inputs: [], outputs: [], wires: [], gates: [] };
  const inputsList = activeGraph.inputs || [];
  const outputsList = activeGraph.outputs || [];
  const gatesList = activeGraph.gates || [];

  // Delay library based on Corner selection
  const CORNER_FACTORS = {
    typical: 1.0,  // Typical corner (TT)
    slow: 1.6,     // Slow corner (SS)
    fast: 0.6      // Fast corner (FF)
  };

  const BASE_GATE_DELAYS = {
    'Sky130_XOR2': 0.22,
    'Sky130_XNOR2': 0.22,
    'Sky130_AND2': 0.15,
    'Sky130_NAND2': 0.12,
    'Sky130_OR2': 0.18,
    'Sky130_NOR2': 0.14,
    'Sky130_NOT': 0.08,
    'Sky130_BUF': 0.06
  };
  const BASE_NET_DELAY = 0.04;

  const factor = CORNER_FACTORS[corner];
  const netDelay = BASE_NET_DELAY * factor;

  // DFS to trace timing paths
  const timingPaths = [];
  const pathDetails = {};

  const tracePath = (currentNet, currentPath, currentDelay, stages) => {
    // If output port is reached, record the finished path
    if (outputsList.includes(currentNet)) {
      const pathId = `path${timingPaths.length + 1}`;
      const totalDelay = currentDelay + netDelay;
      const totalGatesDelay = currentDelay - (currentPath.length - 1) * netDelay;
      const totalNetsDelay = currentPath.length * netDelay;
      const targetClockPeriod = 1.00; // ns
      const slack = targetClockPeriod - totalDelay;

      timingPaths.push({
        id: pathId,
        start: currentPath[0],
        end: currentNet,
        gateDelay: totalGatesDelay,
        netDelay: totalNetsDelay,
        total: totalDelay,
        required: targetClockPeriod,
        slack: slack,
        status: slack >= 0 ? 'MET' : 'VIOLATED'
      });

      pathDetails[pathId] = [
        ...stages,
        {
          pin: `${currentNet} (Output Port)`,
          increment: `${netDelay.toFixed(3)} ns (net)`,
          total: `${totalDelay.toFixed(3)} ns`,
          transition: 'r'
        }
      ];
      return;
    }

    // Find all consumer gates for this net
    const consumerGates = gatesList.filter(g => g.inputs.includes(currentNet));
    consumerGates.forEach(gate => {
      const baseCellDelay = BASE_GATE_DELAYS[gate.type] || 0.10;
      const cellDelay = baseCellDelay * factor;
      const outNet = gate.outputs[0];

      const newStages = [
        ...stages,
        {
          pin: `${gate.name}/in_${currentNet} (${gate.type})`,
          increment: `${netDelay.toFixed(3)} ns (net)`,
          total: `${(currentDelay + netDelay).toFixed(3)} ns`,
          transition: 'r'
        },
        {
          pin: `${gate.name}/out_${outNet} (${gate.type})`,
          increment: `${cellDelay.toFixed(3)} ns (cell)`,
          total: `${(currentDelay + netDelay + cellDelay).toFixed(3)} ns`,
          transition: 'f'
        }
      ];

      tracePath(
        outNet,
        [...currentPath, gate.name],
        currentDelay + netDelay + cellDelay,
        newStages
      );
    });
  };

  // Run path tracing starting from all input ports
  inputsList.forEach(inp => {
    tracePath(inp, [inp], 0, [
      {
        pin: `${inp} (Input Port)`,
        increment: '0.000 ns',
        total: '0.000 ns',
        transition: 'r'
      }
    ]);
  });

  const toggleExpandPath = (id) => {
    if (expandedPathId === id) setExpandedPathId(null);
    else setExpandedPathId(id);
  };

  const isLight = theme === 'light';
  const svgBg = isLight ? '#f1f5f9' : '#05080e';
  const portText = isLight ? '#0f172a' : '#ffffff';

  if (timingPaths.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        backgroundColor: svgBg, 
        color: portText, 
        padding: '20px', 
        alignItems: 'center', 
        justifyContent: 'center', 
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>⚡</span>
          <strong style={{ fontSize: '13px', color: portText }}>Static Timing Analysis Data Not Available</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', maxWidth: '320px' }}>
            Write valid inputs and logic gate configurations in Verilog to perform critical path timing analysis.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--console-bg)', color: 'var(--text-main)', fontSize: '12px' }}>
      {/* Corner selection bar */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--header-bg)', fontSize: '11px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ color: '#64748b' }}><strong>Timing Corner:</strong></span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['typical', 'slow', 'fast'].map((c) => (
              <button 
                key={c}
                onClick={() => { setCorner(c); setExpandedPathId(null); }}
                style={{ 
                  padding: '2px 8px', 
                  background: corner === c ? 'var(--primary-color)' : 'var(--border-color)', 
                  border: 'none', 
                  color: corner === c ? '#fff' : 'var(--text-main)', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontSize: '10px',
                  fontWeight: corner === c ? 'bold' : 'normal'
                }}
              >
                {c === 'typical' ? 'Typical (TT)' : c === 'slow' ? 'Slow (SS)' : 'Fast (FF)'}
              </button>
            ))}
          </div>
        </div>
        <span style={{ color: '#64748b' }}>Target Clock Period: <strong>1.00 ns</strong></span>
      </div>

      {/* Slack table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <th style={{ padding: '8px 12px' }}>Path</th>
              <th style={{ padding: '8px 12px' }}>Startpoint</th>
              <th style={{ padding: '8px 12px' }}>Endpoint</th>
              <th style={{ padding: '8px 12px' }}>Gate (ns)</th>
              <th style={{ padding: '8px 12px' }}>Net (ns)</th>
              <th style={{ padding: '8px 12px' }}>Total (ns)</th>
              <th style={{ padding: '8px 12px' }}>Required (ns)</th>
              <th style={{ padding: '8px 12px' }}>Slack (ns)</th>
              <th style={{ padding: '8px 12px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {timingPaths.map((p) => {
              const isExpanded = expandedPathId === p.id;
              const hasDetails = !!pathDetails[p.id];
              const slackColor = p.slack >= 0 ? '#10b981' : '#ef4444';
              const bg = isExpanded ? 'rgba(59, 130, 246, 0.05)' : 'transparent';

              return (
                <React.Fragment key={p.id}>
                  <tr 
                    onClick={() => hasDetails && toggleExpandPath(p.id)}
                    style={{ 
                      borderBottom: '1px solid var(--border-color)', 
                      backgroundColor: bg,
                      cursor: hasDetails ? 'pointer' : 'default',
                      transition: 'background-color 0.15s'
                    }}
                  >
                    <td style={{ padding: '8px 12px', color: '#60a5fa', fontWeight: 'bold' }}>
                      {p.id} {hasDetails && (isExpanded ? '▼' : '▶')}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{p.start}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{p.end}</td>
                    <td style={{ padding: '8px 12px' }}>{p.gateDelay.toFixed(3)}</td>
                    <td style={{ padding: '8px 12px' }}>{p.netDelay.toFixed(3)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 'bold' }}>{p.total.toFixed(3)}</td>
                    <td style={{ padding: '8px 12px' }}>{p.required.toFixed(3)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 'bold', color: slackColor }}>
                      {p.slack >= 0 ? `+${p.slack.toFixed(3)}` : p.slack.toFixed(3)}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ 
                        fontSize: '9px', 
                        fontWeight: 'bold', 
                        padding: '2px 6px', 
                        borderRadius: '100px', 
                        background: p.slack >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                        color: slackColor
                      }}>
                        {p.status}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded Stage Delay Details */}
                  {isExpanded && hasDetails && (
                    <tr>
                      <td colSpan="9" style={{ padding: '12px 24px', background: 'var(--tab-inactive-bg)', borderBottom: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>
                          Timing Path Net Delay Trace: {p.start} &rarr; {p.end} ({corner} corner)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderLeft: '2px solid var(--border-color)', paddingLeft: '12px' }}>
                          {pathDetails[p.id].map((detail, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '20px', fontFamily: 'monospace', fontSize: '11px', padding: '2px 0' }}>
                              <span style={{ width: '280px', color: 'var(--text-main)' }}>{detail.pin}</span>
                              <span style={{ width: '80px', color: '#94a3b8' }}>{detail.transition === 'r' ? '↑ (rise)' : '↓ (fall)'}</span>
                              <span style={{ width: '120px', color: '#8b5cf6' }}>{detail.increment}</span>
                              <span style={{ width: '100px', color: '#10b981', fontWeight: 'bold' }}>{detail.total}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimingAnalyzer;
